import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import {
  Save, Phone, MapPin, Lock, Eye, EyeOff, Globe,
  Heart, Pill, ChevronDown, ChevronRight, CheckCircle, Edit2, User, Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import PageLayout from '../components/PageLayout';
import SecuritySettings from '../components/SecuritySettings';

const WORK_ROLES = [
  'GYN Surgeon',
  'General Surgeon',
  'Subspecialty Surgeon',
  'Anesthesiologist',
  'CRNA',
  'Surgical Assist / PA',
  'OR RN',
  'PACU RN',
  'Surgical Tech',
  'Translator',
  'Other'
];

const SPANISH_LEVELS = ['None', 'Little', 'Fair', 'Able to translate'];

const Profile = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewTripId = searchParams.get('review');
  const [reviewTripName, setReviewTripName] = useState('');

  // Review mode: which cards have been confirmed
  const REVIEW_CARDS = ['basic', 'workrole', 'language', 'address', 'emergency', 'medical', 'passport'];
  const [confirmedCards, setConfirmedCards] = useState(new Set());
  const [editingCards, setEditingCards] = useState(new Set());
  const isReviewMode = !!reviewTripId;
  const allConfirmed = isReviewMode && REVIEW_CARDS.every(c => confirmedCards.has(c));

  const confirmCard = (card) => {
    setConfirmedCards(prev => new Set([...prev, card]));
    setEditingCards(prev => { const n = new Set(prev); n.delete(card); return n; });
  };
  const editCard = (card) => {
    setEditingCards(prev => new Set([...prev, card]));
    setConfirmedCards(prev => { const n = new Set(prev); n.delete(card); return n; });
  };
  const isCardLocked = (card) => isReviewMode && !confirmedCards.has(card) && !editingCards.has(card);
  const isCardConfirmed = (card) => confirmedCards.has(card);
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    work_roles: [],
    spanish_level: 'None',
    organization: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    phone_country_code: '+1',
    passport_surname: '',
    passport_given_names: '',
    passport_number: '',
    passport_country: '',
    passport_expiration: '',
    emergency_contact_1_name: '',
    emergency_contact_1_phone: '',
    emergency_contact_1_email: '',
    emergency_contact_2_name: '',
    emergency_contact_2_phone: '',
    emergency_contact_2_email: '',
    health_issues: '',
    medications: '',
    glove_size: '',
    shirt_size: '',
    interested_in_excursions: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get('/profile');
        if (res.data) {
          setProfile(prev => ({ ...prev, ...res.data }));
        }
        // Fetch trip name for review mode banner
        if (reviewTripId) {
          try {
            const tripRes = await api.get(`/trips/${reviewTripId}`);
            setReviewTripName(tripRes.data?.name || '');
          } catch {}
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [reviewTripId]);

  const [validationErrors, setValidationErrors] = useState({});

  const validateProfile = () => {
    const errors = {};
    // Basic info — blocks save
    if (!profile.first_name?.trim()) errors.first_name = true;
    if (!profile.last_name?.trim()) errors.last_name = true;
    if (!profile.phone?.trim()) errors.phone = true;
    if (!profile.date_of_birth?.trim()) errors.date_of_birth = true;
    // Address — required by backend calc_profile_completion
    if (!profile.street_address?.trim()) errors.street_address = true;
    if (!profile.city?.trim()) errors.city = true;
    if (!profile.state?.trim()) errors.state = true;
    if (!profile.zip_code?.trim()) errors.zip_code = true;
    // Organization — required by backend
    if (!profile.organization?.trim()) errors.organization = true;
    // Work role — blocks save
    if (!profile.work_roles?.length) errors.work_roles = true;
    // Emergency contact 1 — blocks save
    if (!profile.emergency_contact_1_name?.trim()) errors.emergency_contact_1_name = true;
    if (!profile.emergency_contact_1_phone?.trim()) errors.emergency_contact_1_phone = true;
    if (!profile.emergency_contact_1_email?.trim()) errors.emergency_contact_1_email = true;
    // Health — blocks save
    if (!profile.health_issues?.trim()) errors.health_issues = true;
    return errors;
  };

  const getPassportErrors = () => {
    const errors = {};
    if (!profile.passport_surname?.trim()) errors.passport_surname = true;
    if (!profile.passport_given_names?.trim()) errors.passport_given_names = true;
    if (!profile.passport_country?.trim()) errors.passport_country = true;
    if (!profile.passport_number?.trim()) errors.passport_number = true;
    if (!profile.passport_expiration?.trim()) errors.passport_expiration = true;
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Show which fields are incomplete (visual only — doesn't block save)
    const profileErrors = validateProfile();
    const passportErrors = getPassportErrors();
    setValidationErrors({ ...profileErrors, ...passportErrors });

    const profileUrl = '/profile';

    // Hard-block: US passport must be exactly 9 characters
    const isUS = ['US', 'USA', 'UNITED STATES'].includes((profile.passport_country || '').trim().toUpperCase());
    const pDigits = (profile.passport_number || '').replace(/[\s-]/g, '');
    if (isUS && pDigits.length > 0 && pDigits.length !== 9) {
      toast.error('US passport numbers must be exactly 9 characters');
      return;
    }
    
    setSaving(true);
    
    try {
      await api.put(profileUrl, profile);
      const totalMissing = Object.keys(profileErrors).length + Object.keys(passportErrors).length;
      if (totalMissing > 0) {
        toast.success('Profile saved! Some fields are still incomplete.');
      } else {
        toast.success('Profile saved successfully!');
      }
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptInvitation = async () => {
    setSaving(true);
    try {
      await api.put('/profile', profile);
      // Update participant_status from "invited" to "registered"
      await api.put(`/trips/${reviewTripId}/accept-invitation`);
      toast.success('Invitation accepted! Welcome to the team.');
      navigate('/dashboard', { replace: true });
    } catch (error) {
      toast.error('Failed to confirm invitation');
    } finally {
      setSaving(false);
    }
  };


  const handleRoleChange = (role, checked) => {
    setProfile(prev => ({
      ...prev,
      work_roles: checked 
        ? [...prev.work_roles, role]
        : prev.work_roles.filter(r => r !== role)
    }));
    if (checked) setValidationErrors(prev => ({ ...prev, work_roles: undefined }));
  };

  // Accept raw input while typing — format on blur to avoid cursor issues
  const handlePhoneChange = (raw) => {
    setProfile(prev => ({ ...prev, phone: raw }));
  };
  const handlePhoneBlur = () => {
    if ((profile.phone_country_code || '+1') !== '+1') return;
    const digits = (profile.phone || '').replace(/\D/g, '').slice(0, 10);
    let formatted = digits;
    if (digits.length > 6) formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    else if (digits.length > 3) formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    setProfile(prev => ({ ...prev, phone: formatted }));
  };

  // Passport number validation for US passports
  const isUSPassport = ['US', 'USA', 'UNITED STATES'].includes((profile.passport_country || '').trim().toUpperCase());
  const passportDigits = (profile.passport_number || '').replace(/[\s-]/g, '');
  const passportLengthError = isUSPassport && passportDigits.length > 0 && passportDigits.length !== 9;

  // Error styling helper
  const fieldError = (fieldName) => validationErrors[fieldName]
    ? { 'data-error': 'true', className: 'border-red-400 ring-1 ring-red-400' }
    : {};
  const labelReq = (text, fieldName) => (
    <Label htmlFor={fieldName} className="flex items-center gap-1">
      {text} <span className="text-red-500">*</span>
      {validationErrors[fieldName] && <span className="text-xs text-red-500 font-normal ml-1">Required</span>}
    </Label>
  );

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const [credForm, setCredForm] = useState({ currentPassword: '', newEmail: '', newPassword: '', confirmPassword: '' });
  const [savingCreds, setSavingCreds] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [credsOpen, setCredsOpen] = useState(false);

  const handleChangeCredentials = async () => {
    if (!credForm.currentPassword) { toast.error('Current password is required'); return; }
    if (!credForm.newEmail && !credForm.newPassword) { toast.error('Enter a new email or new password (or both)'); return; }
    if (credForm.newPassword && credForm.newPassword !== credForm.confirmPassword) { toast.error('New passwords do not match'); return; }
    if (credForm.newPassword && credForm.newPassword.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    setSavingCreds(true);
    try {
      const res = await api.put('/auth/change-credentials', {
        current_password: credForm.currentPassword,
        new_email: credForm.newEmail || undefined,
        new_password: credForm.newPassword || undefined,
      });
      toast.success(res.data.message);
      setCredForm({ currentPassword: '', newEmail: '', newPassword: '', confirmPassword: '' });
      if (credForm.newEmail) {
        toast.info('Email changed — please sign in again with your new email.');
        setTimeout(() => { window.location.href = '/login'; }, 2000);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update credentials');
    } finally {
      setSavingCreds(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  const saveBtn = isReviewMode ? (
    allConfirmed ? (
      <Button onClick={handleAcceptInvitation} disabled={saving} className="rounded-full bg-green-600 hover:bg-green-700" data-testid="accept-invitation-btn">
        <CheckCircle className="w-4 h-4 mr-2" />{saving ? 'Confirming...' : 'Confirm & Accept Invitation'}
      </Button>
    ) : (
      <span className="text-sm text-slate-400">{confirmedCards.size}/{REVIEW_CARDS.length} sections reviewed</span>
    )
  ) : (
    <Button onClick={handleSubmit} disabled={saving} className="rounded-full bg-rojo hover:bg-rojo-dark" data-testid="save-profile-btn">
      <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save My Progress'}
    </Button>
  );

  // Review mode card wrapper — using a render function (NOT a component)
  // to avoid remounting on every state change which causes focus loss
  const wrapReviewCard = (cardKey, children) => {
    if (!isReviewMode) return children;
    const locked = isCardLocked(cardKey);
    const confirmed = isCardConfirmed(cardKey);
    return (
      <div className="relative" key={`review-${cardKey}`}>
        {locked && (
          <div className="absolute inset-0 bg-white/60 z-10 rounded-xl flex items-end justify-center pb-4 border-2 border-dashed border-slate-300">
            <div className="flex gap-2">
              <Button size="sm" className="rounded-full bg-green-600 hover:bg-green-700 h-8 text-xs shadow-lg"
                onClick={() => confirmCard(cardKey)} data-testid={`confirm-${cardKey}`}>
                <CheckCircle className="w-3.5 h-3.5 mr-1" />This is correct
              </Button>
              <Button size="sm" variant="outline" className="rounded-full h-8 text-xs bg-white shadow-lg"
                onClick={() => editCard(cardKey)} data-testid={`edit-${cardKey}`}>
                <Edit2 className="w-3.5 h-3.5 mr-1" />Edit
              </Button>
            </div>
          </div>
        )}
        {confirmed && (
          <div className="absolute top-3 right-3 z-10">
            <span className="flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
              <CheckCircle className="w-3 h-3" />Confirmed
            </span>
          </div>
        )}
        <div className={confirmed ? 'ring-2 ring-green-300 rounded-xl' : ''}>
          {children}
        </div>
      </div>
    );
  };

  return (
    <PageLayout title="My Profile" headerActions={saveBtn} maxWidth="max-w-4xl">
          {/* Review mode banner */}
          {isReviewMode && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl" data-testid="review-banner">
              <p className="text-sm font-medium text-blue-800">
                You've been invited to <strong>{reviewTripName || 'a trip'}</strong> — please review your profile and confirm your information is accurate.
              </p>
              <p className="text-xs text-blue-600 mt-1">{confirmedCards.size} of {REVIEW_CARDS.length} sections reviewed</p>
            </div>
          )}
          <form onSubmit={handleSubmit} noValidate className="space-y-8">
            {/* Basic Information */}
            {wrapReviewCard("basic",
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading">
                  <User className="w-5 h-5 text-primary" />
                  Basic Information
                </CardTitle>
                <CardDescription>Your personal details</CardDescription>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 gap-4">
                {/* Email — read-only, from login account */}
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={user?.email || ''}
                    readOnly
                    className="bg-slate-50 text-slate-600 cursor-default"
                  />
                  <p className="text-xs text-slate-400">Login email — contact admin to change</p>
                </div>
                <div className="space-y-2">
                  {labelReq('First Name', 'first_name')}
                  <Input
                    id="first_name"
                    data-testid="first-name-input"
                    value={profile.first_name}
                    onChange={(e) => setProfile({...profile, first_name: e.target.value})}
                    placeholder="John"
                    {...fieldError('first_name')}
                  />
                </div>
                <div className="space-y-2">
                  {labelReq('Last Name', 'last_name')}
                  <Input
                    id="last_name"
                    data-testid="last-name-input"
                    value={profile.last_name}
                    onChange={(e) => setProfile({...profile, last_name: e.target.value})}
                    placeholder="Doe"
                    {...fieldError('last_name')}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    Date of Birth <span className="text-red-500">*</span>
                    {validationErrors.date_of_birth && <span className="text-xs text-red-500 font-normal ml-1">Required</span>}
                  </Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <Select
                      value={profile.date_of_birth ? profile.date_of_birth.substring(0, 4) : ''}
                      onValueChange={(y) => {
                        const m = profile.date_of_birth?.substring(5, 7) || '01';
                        const d = profile.date_of_birth?.substring(8, 10) || '01';
                        setProfile({ ...profile, date_of_birth: `${y}-${m}-${d}` });
                      }}
                    >
                      <SelectTrigger data-testid="dob-year"><SelectValue placeholder="Year" /></SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 90 }, (_, i) => new Date().getFullYear() - 10 - i).map(y => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={profile.date_of_birth ? profile.date_of_birth.substring(5, 7) : ''}
                      onValueChange={(m) => {
                        const y = profile.date_of_birth?.substring(0, 4) || '1980';
                        const d = profile.date_of_birth?.substring(8, 10) || '01';
                        setProfile({ ...profile, date_of_birth: `${y}-${m}-${d}` });
                      }}
                    >
                      <SelectTrigger data-testid="dob-month"><SelectValue placeholder="Month" /></SelectTrigger>
                      <SelectContent>
                        {['January','February','March','April','May','June','July','August','September','October','November','December'].map((name, i) => (
                          <SelectItem key={i} value={String(i + 1).padStart(2, '0')}>{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={profile.date_of_birth ? profile.date_of_birth.substring(8, 10) : ''}
                      onValueChange={(d) => {
                        const y = profile.date_of_birth?.substring(0, 4) || '1980';
                        const m = profile.date_of_birth?.substring(5, 7) || '01';
                        setProfile({ ...profile, date_of_birth: `${y}-${m}-${d}` });
                      }}
                    >
                      <SelectTrigger data-testid="dob-day"><SelectValue placeholder="Day" /></SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                          <SelectItem key={d} value={String(d).padStart(2, '0')}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  {labelReq('Mobile Phone', 'phone')}
                  <div className="flex gap-2">
                    <Input
                      data-testid="phone-country-code"
                      value={profile.phone_country_code || '+1'}
                      onChange={(e) => setProfile({...profile, phone_country_code: e.target.value})}
                      className="w-[72px] text-center flex-shrink-0"
                      placeholder="+1"
                    />
                    <Input
                      id="phone"
                      data-testid="phone-input"
                      value={profile.phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      onBlur={handlePhoneBlur}
                      placeholder={(profile.phone_country_code || '+1') === '+1' ? '555-123-4567' : 'Phone number'}
                      className={`flex-1 ${validationErrors.phone ? 'border-red-400 ring-1 ring-red-400' : ''}`}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Work Role */}
            {wrapReviewCard("workrole",
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading">
                  <Briefcase className="w-5 h-5 text-primary" />
                  Work Role <span className="text-red-500 text-base">*</span>
                </CardTitle>
                <CardDescription>
                  What role(s) will you fill on the team?
                  {validationErrors.work_roles && <span className="text-red-500 ml-2 font-medium">Please select at least one role</span>}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {WORK_ROLES.map((role) => (
                    <div key={role} className="flex items-center space-x-2">
                      <Checkbox
                        id={role}
                        checked={profile.work_roles.includes(role)}
                        onCheckedChange={(checked) => handleRoleChange(role, checked)}
                      />
                      <Label htmlFor={role} className="text-sm cursor-pointer">{role}</Label>
                    </div>
                  ))}
                </div>

                <div className="grid sm:grid-cols-3 gap-4 pt-2">
                  <div className="space-y-2">
                    {labelReq('Hospital / Organization', 'organization')}
                    <Input
                      id="organization"
                      value={profile.organization}
                      onChange={(e) => setProfile({...profile, organization: e.target.value})}
                      placeholder="Mayo Clinic"
                      {...fieldError('organization')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="glove_size">Sterile Glove Size</Label>
                    <Input
                      id="glove_size"
                      value={profile.glove_size}
                      onChange={(e) => setProfile({...profile, glove_size: e.target.value})}
                      placeholder="e.g. 7.5"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="shirt_size">Shirt Size</Label>
                    <Select
                      value={profile.shirt_size || ''}
                      onValueChange={(v) => setProfile({...profile, shirt_size: v})}
                    >
                      <SelectTrigger id="shirt_size" data-testid="shirt-size-select">
                        <SelectValue placeholder="Select size..." />
                      </SelectTrigger>
                      <SelectContent>
                        {['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'].map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Language & Trip Preferences */}
            {wrapReviewCard("language",
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading">
                  <Globe className="w-5 h-5 text-primary" />
                  Language & Trip Preferences
                </CardTitle>
                <CardDescription>Help us plan the best experience for you.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="spanish_level">Spanish Speaking Level</Label>
                  <Select
                    value={profile.spanish_level}
                    onValueChange={(value) => setProfile({...profile, spanish_level: value})}
                  >
                    <SelectTrigger className="max-w-xs" id="spanish_level">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent>
                      {SPANISH_LEVELS.map((level) => (
                        <SelectItem key={level} value={level}>{level}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Interested in Excursions?</Label>
                  <p className="text-xs text-slate-500">
                    We occasionally arrange optional group excursions (e.g., day trips, cultural experiences) on the free day.
                  </p>
                  <div className="flex items-center gap-4 pt-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="excursions"
                        checked={profile.interested_in_excursions === true}
                        onChange={() => setProfile({...profile, interested_in_excursions: true})}
                        className="accent-primary"
                        data-testid="excursions-yes"
                      />
                      <span className="text-sm text-slate-700">Yes, I'm interested</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="excursions"
                        checked={profile.interested_in_excursions === false}
                        onChange={() => setProfile({...profile, interested_in_excursions: false})}
                        className="accent-primary"
                        data-testid="excursions-no"
                      />
                      <span className="text-sm text-slate-700">No, not this time</span>
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Address */}
            {wrapReviewCard("address",
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading">
                  <MapPin className="w-5 h-5 text-primary" />
                  Home Address
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  {labelReq('Street Address', 'street_address')}
                  <Input
                    id="street_address"
                    data-testid="street-address-input"
                    value={profile.street_address}
                    onChange={(e) => setProfile({...profile, street_address: e.target.value})}
                    placeholder="123 Main St"
                    {...fieldError('street_address')}
                  />
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    {labelReq('City', 'city')}
                    <Input
                      id="city"
                      data-testid="city-input"
                      value={profile.city}
                      onChange={(e) => setProfile({...profile, city: e.target.value})}
                      placeholder="Minneapolis"
                      {...fieldError('city')}
                    />
                  </div>
                  <div className="space-y-2">
                    {labelReq('State', 'state')}
                    <Input
                      id="state"
                      data-testid="state-input"
                      value={profile.state}
                      onChange={(e) => setProfile({...profile, state: e.target.value})}
                      placeholder="MN"
                      maxLength={2}
                      {...fieldError('state')}
                    />
                  </div>
                  <div className="space-y-2">
                    {labelReq('ZIP Code', 'zip_code')}
                    <Input
                      id="zip_code"
                      data-testid="zip-input"
                      value={profile.zip_code}
                      onChange={(e) => setProfile({...profile, zip_code: e.target.value})}
                      placeholder="55401"
                      {...fieldError('zip_code')}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Emergency Contacts */}
            {wrapReviewCard("emergency",
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading">
                  <Heart className="w-5 h-5 text-primary" />
                  Emergency Contacts
                </CardTitle>
                <CardDescription>This information will be shared with the team leader</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Contact 1 */}
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-3">Contact 1 <span className="text-red-500">*</span></p>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      {labelReq('Name', 'emergency_contact_1_name')}
                      <Input
                        id="emergency_contact_1_name"
                        value={profile.emergency_contact_1_name}
                        onChange={(e) => setProfile({...profile, emergency_contact_1_name: e.target.value})}
                        placeholder="Jane Doe"
                        {...fieldError('emergency_contact_1_name')}
                      />
                    </div>
                    <div className="space-y-2">
                      {labelReq('Phone', 'emergency_contact_1_phone')}
                      <Input
                        id="emergency_contact_1_phone"
                        value={profile.emergency_contact_1_phone}
                        onChange={(e) => setProfile({...profile, emergency_contact_1_phone: e.target.value})}
                        placeholder="555-123-4567"
                        {...fieldError('emergency_contact_1_phone')}
                      />
                    </div>
                    <div className="space-y-2">
                      {labelReq('Email', 'emergency_contact_1_email')}
                      <Input
                        id="emergency_contact_1_email"
                        type="email"
                        value={profile.emergency_contact_1_email}
                        onChange={(e) => setProfile({...profile, emergency_contact_1_email: e.target.value})}
                        placeholder="jane@example.com"
                        {...fieldError('emergency_contact_1_email')}
                      />
                    </div>
                  </div>
                </div>

                {/* Contact 2 */}
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-3">Contact 2</p>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="emergency_contact_2_name">Name</Label>
                      <Input
                        id="emergency_contact_2_name"
                        value={profile.emergency_contact_2_name}
                        onChange={(e) => setProfile({...profile, emergency_contact_2_name: e.target.value})}
                        placeholder="Bob Smith"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="emergency_contact_2_phone">Phone</Label>
                      <Input
                        id="emergency_contact_2_phone"
                        value={profile.emergency_contact_2_phone}
                        onChange={(e) => setProfile({...profile, emergency_contact_2_phone: e.target.value})}
                        placeholder="555-987-6543"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="emergency_contact_2_email">Email</Label>
                      <Input
                        id="emergency_contact_2_email"
                        type="email"
                        value={profile.emergency_contact_2_email}
                        onChange={(e) => setProfile({...profile, emergency_contact_2_email: e.target.value})}
                        placeholder="bob@example.com"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Medical Information */}
            {wrapReviewCard("medical",
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading">
                  <Pill className="w-5 h-5 text-primary" />
                  Medical Information
                </CardTitle>
                <CardDescription>Confidential - shared only with trip leaders</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  {labelReq('Health Issues', 'health_issues')}
                  <Textarea
                    id="health_issues"
                    value={profile.health_issues}
                    onChange={(e) => setProfile({...profile, health_issues: e.target.value})}
                    placeholder='List any health conditions, or type "None"'
                    rows={3}
                    {...fieldError('health_issues')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="medications">Current Medications & Doses</Label>
                  <Textarea
                    id="medications"
                    value={profile.medications}
                    onChange={(e) => setProfile({...profile, medications: e.target.value})}
                    placeholder="List medications and dosages..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
            )}

            {/* ── Section Divider: Profile ↑ / Passport ↓ ── */}
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-300" /></div>
              <div className="relative flex justify-center">
                <span className="bg-background px-4 text-sm font-medium text-slate-400">Passport Information</span>
              </div>
            </div>

            {/* Passport */}
            {wrapReviewCard("passport",
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading">
                  <Globe className="w-5 h-5 text-primary" />
                  Passport Details
                </CardTitle>
                <CardDescription>As it appears on your passport. You can save your profile and come back to complete this when you have your passport handy.</CardDescription>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  {labelReq('Surname (last name)', 'passport_surname')}
                  <Input
                    id="passport_surname"
                    value={profile.passport_surname}
                    onChange={(e) => setProfile({...profile, passport_surname: e.target.value})}
                    placeholder="DOE"
                    {...fieldError('passport_surname')}
                  />
                </div>
                <div className="space-y-2">
                  {labelReq('Given Names (first & middle)', 'passport_given_names')}
                  <Input
                    id="passport_given_names"
                    value={profile.passport_given_names}
                    onChange={(e) => setProfile({...profile, passport_given_names: e.target.value})}
                    placeholder="JOHN MICHAEL"
                    {...fieldError('passport_given_names')}
                  />
                </div>
                <div className="space-y-2">
                  {labelReq('Country', 'passport_country')}
                  <Input
                    id="passport_country"
                    value={profile.passport_country}
                    onChange={(e) => setProfile({...profile, passport_country: e.target.value})}
                    placeholder="USA"
                    {...fieldError('passport_country')}
                  />
                </div>
                <div className="space-y-2">
                  {labelReq('Passport Number', 'passport_number')}
                  <Input
                    id="passport_number"
                    value={profile.passport_number}
                    onChange={(e) => setProfile({...profile, passport_number: e.target.value})}
                    placeholder={isUSPassport ? '9 characters required' : '123456789'}
                    {...fieldError('passport_number')}
                    className={passportLengthError ? 'border-red-400 ring-1 ring-red-400' : ''}
                  />
                  {passportLengthError && (
                    <p className="text-xs text-red-500">US passport numbers must be exactly 9 characters ({passportDigits.length} entered)</p>
                  )}
                </div>
                <div className="space-y-2">
                  {labelReq('Expiration Date', 'passport_expiration')}
                  <Input
                    id="passport_expiration"
                    type="date"
                    value={profile.passport_expiration}
                    onChange={(e) => setProfile({...profile, passport_expiration: e.target.value})}
                    {...fieldError('passport_expiration')}
                  />
                </div>
              </CardContent>
            </Card>
            )}

            {/* ── Section Divider: Passport ↑ / Account Settings ↓ ── */}
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-300" /></div>
              <div className="relative flex justify-center">
                <span className="bg-background px-4 text-sm font-medium text-slate-400">Account Settings</span>
              </div>
            </div>

            {/* Security Settings (2FA) — admin only */}
            {isAdmin && <SecuritySettings />}

            {/* Change Credentials — collapsible */}
            <Card>
              <button
                type="button"
                onClick={() => setCredsOpen(o => !o)}
                className="w-full text-left"
                data-testid="toggle-credentials-section"
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-heading">
                    <Lock className="w-5 h-5 text-primary" />
                    Change Email or Password
                    {credsOpen
                      ? <ChevronDown className="w-4 h-4 ml-auto text-slate-400" />
                      : <ChevronRight className="w-4 h-4 ml-auto text-slate-400" />}
                  </CardTitle>
                  <CardDescription>
                    {credsOpen
                      ? 'Only use this if you need to update your login email or password.'
                      : 'Your current login is working — expand this only if you need to change your email or password.'}
                  </CardDescription>
                </CardHeader>
              </button>
              {credsOpen && (
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Current Password *</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords ? 'text' : 'password'}
                        value={credForm.currentPassword}
                        onChange={e => setCredForm(f => ({ ...f, currentPassword: e.target.value }))}
                        placeholder="Your current password"
                        className="pr-10"
                        data-testid="current-password-input"
                      />
                      <button type="button" onClick={() => setShowPasswords(s => !s)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>New Email <span className="text-slate-400 font-normal text-xs">(optional)</span></Label>
                      <Input
                        type="email"
                        value={credForm.newEmail}
                        onChange={e => setCredForm(f => ({ ...f, newEmail: e.target.value }))}
                        placeholder={user?.email}
                        data-testid="new-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>New Password <span className="text-slate-400 font-normal text-xs">(optional)</span></Label>
                      <Input
                        type={showPasswords ? 'text' : 'password'}
                        value={credForm.newPassword}
                        onChange={e => setCredForm(f => ({ ...f, newPassword: e.target.value }))}
                        placeholder="Min. 6 characters"
                        data-testid="new-password-creds-input"
                      />
                    </div>
                    {credForm.newPassword && (
                      <div className="space-y-2">
                        <Label>Confirm New Password</Label>
                        <Input
                          type={showPasswords ? 'text' : 'password'}
                          value={credForm.confirmPassword}
                          onChange={e => setCredForm(f => ({ ...f, confirmPassword: e.target.value }))}
                          placeholder="Repeat new password"
                          data-testid="confirm-new-password-input"
                        />
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    onClick={handleChangeCredentials}
                    disabled={savingCreds || !credForm.currentPassword}
                    variant="outline"
                    className="rounded-full"
                    data-testid="save-credentials-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingCreds ? 'Updating...' : 'Update Credentials'}
                  </Button>
                  {credForm.newEmail && (
                    <p className="text-xs text-amber-600">Changing your email will sign you out — you'll log in with the new email.</p>
                  )}
                </CardContent>
              )}
            </Card>

            <div className="flex justify-end gap-4 pt-4">
              <Link to="/dashboard">
                <Button variant="outline" type="button" className="rounded-full">
                  Cancel
                </Button>
              </Link>
              <Button 
                type="submit" 
                disabled={saving}
                className="rounded-full bg-rojo hover:bg-rojo-dark"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save My Progress'}
              </Button>
            </div>
          </form>
    </PageLayout>
  );
};

export default Profile;
